<template>
  <div>
    <div class="overflow-x-auto">
      <table class="text-sm table-fixed w-full" :style="{ minWidth: tableWidth + 'px' }">
        <colgroup>
          <col v-if="bulkMode" style="width: 36px" />
          <col v-for="col in columns" :key="col.key" :style="{ width: widthFor(col.key) + 'px' }" />
        </colgroup>
        <thead>
          <tr class="border-b border-gray-200 bg-gray-50">
            <th v-if="bulkMode" class="px-3 py-2.5">
              <input
                type="checkbox"
                :checked="allChecked"
                :indeterminate="someChecked && !allChecked"
                class="rounded border-gray-300 h-3 w-3"
                @change="$emit('toggle-all', allChecked ? [] : pagedItems.filter((i) => !isAncestorOnly(i)).map((i) => i.id))"
              />
            </th>
            <th
              v-for="(col, ci) in columns"
              :key="col.key"
              class="relative px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap select-none group/th"
              :class="[
                col.sortable === false ? '' : 'cursor-pointer hover:text-gray-700',
                ci < columns.length - 1 ? 'border-r border-gray-200' : '',
              ]"
              @click="col.sortable === false ? null : $emit('sort', col.key)"
            >
              <span class="truncate">{{ col.label }}</span>
              <span v-if="sortField === col.key" class="ml-1">
                {{ sortDir === 'asc' ? '↑' : '↓' }}
              </span>
              <span
                v-if="ci < columns.length - 1"
                class="absolute top-0 right-0 h-full w-2 translate-x-1/2 cursor-col-resize z-10 flex justify-center"
                :title="`Drag to resize — double-click to reset ${col.label || 'column'}`"
                @click.stop
                @mousedown.stop.prevent="startResize($event, col.key)"
                @dblclick.stop.prevent="resetWidth(col.key)"
              >
                <span
                  class="w-px h-full bg-transparent group-hover/th:bg-gray-300 transition-colors"
                  :class="resizingKey === col.key ? '!bg-primary-500' : ''"
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in pagedItems"
            :key="item.id"
            class="group border-b border-gray-100 cursor-pointer transition-colors"
            :class="[
              item.id === selectedId ? 'bg-primary-50' : 'hover:bg-gray-50',
              item.id === keyboardFocusedId ? 'ring-1 ring-inset ring-primary-300' : '',
            ]"
            @click="onRowClick(item, $event)"
            @auxclick="onRowClick(item, $event)"
          >
            <td v-if="bulkMode" class="px-3 py-2.5" @click.stop>
              <input
                v-if="!isAncestorOnly(item)"
                type="checkbox"
                :checked="checkedIds.has(item.id)"
                class="rounded border-gray-300 h-3 w-3"
                @change="$emit('toggle-check', item.id)"
              />
            </td>
            <!-- ID -->
            <td class="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap truncate">
              #{{ item.id }}
            </td>
            <!-- Title + tags -->
            <td class="px-4 py-2.5 overflow-hidden">
              <div class="flex items-center gap-1.5 flex-wrap">
                <span v-if="parentName(item.parent)" class="text-[10px] text-gray-400 truncate max-w-[120px]">
                  {{ parentName(item.parent) }}
                  <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-[7px] mx-0.5" />
                </span>
                <span
                  class="sensitive"
                  :class="isAncestorOnly(item) ? 'text-xs text-gray-400' : 'text-sm text-gray-900'"
                >{{ item.title }}</span>
                <FontAwesomeIcon
                  v-if="isAncestorOnly(item)"
                  :icon="['fas', 'chevron-right']"
                  class="text-[8px] text-gray-300 shrink-0"
                />
                <span
                  v-if="!item.customer_facing && !isAncestorOnly(item)"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0"
                >Internal</span>
                <span
                  v-if="isAwaitingStaffReply(item) && !isAncestorOnly(item)"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0"
                  :title="customerRepliedLabel(item)"
                >Customer replied</span>
                <span
                  v-for="tag in item.tags || []"
                  :key="tag"
                  class="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0"
                >
                  {{ tag }}
                </span>
              </div>
            </td>
            <!-- Type -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <span
                class="text-[11px] font-medium px-1.5 py-0.5 rounded"
                :class="typeClass(item.requirement_type_name)"
              >
                {{ item.requirement_type_name }}
              </span>
            </td>
            <!-- Tenant -->
            <td class="px-4 py-2.5 whitespace-nowrap overflow-hidden" @click.stop>
              <Support_InlineDropdown
                v-if="!isAncestorOnly(item)"
                :model-value="item.tenant"
                :options="tenantDropdownOptions"
                :chip-style="item.tenant_name ? tenantStyle(item.tenant_name) : null"
                :chip-class="item.tenant_name ? '' : 'text-gray-400 bg-gray-50'"
                :display-label="item.tenant_name || 'No tenant'"
                @update:model-value="$emit('update-field', { id: item.id, tenant: $event })"
              />
            </td>
            <!-- Status -->
            <td class="px-4 py-2.5 whitespace-nowrap" @click.stop>
              <Support_InlineDropdown
                v-if="!isAncestorOnly(item)"
                :model-value="item.status"
                :options="statusOptionsFor(item)"
                :chip-class="statusClass(item.status)"
                :display-label="statusLabel(item.status)"
                @update:model-value="$emit('update-field', { id: item.id, status: $event })"
              />
            </td>
            <!-- Priority -->
            <td class="px-4 py-2.5 whitespace-nowrap" @click.stop>
              <Support_InlineDropdown
                v-if="!isAncestorOnly(item)"
                :model-value="priorityToCode(item.priority)"
                :options="priorityOptions"
                :chip-class="priorityClass(item.priority)"
                :display-label="priorityLabel(item.priority)"
                @update:model-value="$emit('update-field', { id: item.id, priority: $event })"
              />
            </td>
            <!-- Due Date -->
            <td class="px-4 py-2.5 whitespace-nowrap text-xs" :class="isAncestorOnly(item) ? '' : dueDateClass(item.due_date)">
              {{ isAncestorOnly(item) ? '' : formatDueDate(item.due_date) }}
            </td>
            <!-- Parent -->
            <td class="px-4 py-2.5 text-xs truncate" :class="isAncestorOnly(item) ? 'text-gray-400' : 'text-gray-500'">
              {{ parentName(item.parent) }}
            </td>
            <!-- Owner -->
            <td class="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap truncate" @click.stop>
              <Support_InlineDropdown
                v-if="!isAncestorOnly(item)"
                :model-value="item.owner"
                :options="ownerDropdownOptions"
                :display-label="item.owner_name || 'Unassigned'"
                chip-class="text-gray-500 bg-transparent hover:bg-gray-100"
                @update:model-value="$emit('update-field', { id: item.id, owner: $event })"
              />
            </td>
            <!-- Requester -->
            <td class="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap truncate sensitive">
              {{ isAncestorOnly(item) ? '' : item.requester_name || '' }}
            </td>
            <!-- Comments + actions -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <span
                  v-if="!isAncestorOnly(item) && commentCount(item)"
                  class="inline-flex items-center gap-1 text-xs text-gray-400"
                >
                  <FontAwesomeIcon :icon="['far', 'comment']" class="text-[11px]" />
                  {{ commentCount(item) }}
                </span>
                <button
                  v-if="!isAncestorOnly(item)"
                  class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity ml-auto"
                  aria-label="Row actions"
                  @click.stop="$emit('actions-menu', { event: $event, node: item })"
                >
                  <FontAwesomeIcon :icon="['fas', 'ellipsis']" class="text-xs" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-xs text-gray-500">
      <span>{{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, items.length) }} of {{ items.length }}</span>
      <div class="flex gap-1">
        <button
          v-for="page in pageRange"
          :key="page"
          class="px-2 py-1 rounded"
          :class="page === currentPage ? 'bg-primary-500 text-white' : 'hover:bg-gray-100'"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { z } from 'zod';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Support_InlineDropdown from './Support_InlineDropdown.vue';
import {
  PRIORITY_CLASSES,
  PRIORITY_CODES,
  PRIORITY_SHORT_LABELS,
  selectableStatusesFor,
  STATUS_CLASSES,
  STATUS_LABELS,
  TYPE_CLASSES,
  awaitingStaffReply,
  customerRepliedLabel,
  daysUntil,
  isDueThisBusinessWeek,
  formatDateValue,
  priorityClass as priorityClassFor,
  priorityShortLabel,
  priorityToCode,
  tenantColorFor,
} from './support_constants';
import { BrowserStore } from '@/utils/BrowserStore';
import { isPlainClick, openInNewTabOnModifiedClick } from '@/utils/links';

const MIN_COLUMN_WIDTH = 56;

const COLUMNS = [
  { key: 'id', label: 'ID', width: 80 },
  { key: 'title', label: 'Title', width: 380 },
  { key: 'requirement_type_name', label: 'Type', width: 120 },
  { key: 'tenant_name', label: 'Tenant', width: 130 },
  { key: 'status', label: 'Status', width: 110 },
  { key: 'priority', label: 'Priority', width: 90 },
  { key: 'due_date', label: 'Due Date', width: 120 },
  { key: 'parent', label: 'Parent', width: 180 },
  { key: 'owner_name', label: 'Owner', width: 130 },
  { key: 'requester_name', label: 'Requester', width: 130 },
  { key: 'comments', label: '', width: 80, sortable: false },
];

const colWidthsStore = new BrowserStore(
  'support_list_column_widths',
  z.object(
    Object.fromEntries(
      COLUMNS.map((c) => [
        c.key,
        z.number().min(MIN_COLUMN_WIDTH).catch(c.width).default(c.width),
      ]),
    ),
  ),
);

export default {
  name: 'Support_Table',
  components: { FontAwesomeIcon, Support_InlineDropdown },
  props: {
    items: { type: Array, required: true },
    allItems: { type: Array, required: true },
    selectedId: { type: Number, default: null },
    keyboardFocusedId: { type: Number, default: null },
    commentCounts: { type: Object, default: () => ({}) },
    filterMatchIds: { type: Set, default: () => new Set() },
    hasActiveFilters: { type: Boolean, default: false },
    sortField: { type: String, default: null },
    sortDir: { type: String, default: 'asc' },
    bulkMode: { type: Boolean, default: false },
    checkedIds: { type: Set, default: () => new Set() },
    tenantOptions: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    // Changes whenever the filter/search/sort state changes; the page resets on
    // that, never on a data refresh.
    pageResetKey: { type: String, default: '' },
  },
  emits: ['select', 'sort', 'update-field', 'toggle-check', 'toggle-all', 'actions-menu'],
  data() {
    return {
      currentPage: 1,
      pageSize: 50,
      resizingKey: null,
      columnWidths: colWidthsStore.load(),
      columns: COLUMNS,
    };
  },
  computed: {
    parentMap() {
      const map = {};
      for (const item of this.allItems) {
        map[item.id] = item.title;
      }
      return map;
    },
    tableWidth() {
      return this.columns.reduce((sum, col) => sum + this.widthFor(col.key), 0) + (this.bulkMode ? 36 : 0);
    },
    totalPages() {
      return Math.ceil(this.items.length / this.pageSize);
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.items.slice(start, start + this.pageSize);
    },
    pageRange() {
      const range = [];
      const delta = 2;
      const left = Math.max(1, this.currentPage - delta);
      const right = Math.min(this.totalPages, this.currentPage + delta);
      for (let i = left; i <= right; i++) range.push(i);
      return range;
    },
    allChecked() {
      const checkable = this.pagedItems.filter((i) => !this.isAncestorOnly(i));
      return checkable.length > 0 && checkable.every((i) => this.checkedIds.has(i.id));
    },
    someChecked() {
      return this.pagedItems.some((i) => this.checkedIds.has(i.id));
    },
    tenantDropdownOptions() {
      return this.tenantOptions.map((t) => ({ value: t.id, label: t.name, chipStyle: tenantColorFor(t.name) }));
    },
    priorityOptions() {
      return PRIORITY_CODES.map((code) => ({ value: code, label: PRIORITY_SHORT_LABELS[code], chipClass: PRIORITY_CLASSES[code] }));
    },
    ownerDropdownOptions() {
      const options = [{ value: null, label: 'Unassigned', chipClass: 'text-gray-400' }];
      for (const u of this.users) {
        options.push({ value: u.id, label: u.full_name || u.email, chipClass: 'text-gray-700' });
      }
      return options;
    },
  },
  watch: {
    pageResetKey() {
      this.currentPage = 1;
    },
    totalPages(total) {
      if (this.currentPage > total) this.currentPage = Math.max(1, total);
    },
  },
  beforeUnmount() {
    this.stopResize();
  },
  methods: {
    detailHref(item) {
      const name = this.$route.meta.supportTab === 'service-desk' ? 'Support-ServiceDeskDetail' : 'Support-WorkDetail';
      return this.$router.resolve({ name, params: { selectedId: item.id }, query: this.$route.query }).href;
    },
    onRowClick(item, event) {
      if (isPlainClick(event)) this.$emit('select', item);
      else openInNewTabOnModifiedClick(event, this.detailHref(item));
    },
    isAwaitingStaffReply: awaitingStaffReply,
    customerRepliedLabel,
    priorityToCode,
    widthFor(key) {
      return this.columnWidths[key];
    },
    startResize(event, key) {
      this.resizingKey = key;
      this._resizeStartX = event.clientX;
      this._resizeStartWidth = this.widthFor(key);
      window.addEventListener('mousemove', this.onResizeMove);
      window.addEventListener('mouseup', this.stopResize);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    onResizeMove(event) {
      if (!this.resizingKey) return;
      const next = Math.max(
        MIN_COLUMN_WIDTH,
        Math.round(this._resizeStartWidth + (event.clientX - this._resizeStartX)),
      );
      this.columnWidths = { ...this.columnWidths, [this.resizingKey]: next };
    },
    stopResize() {
      window.removeEventListener('mousemove', this.onResizeMove);
      window.removeEventListener('mouseup', this.stopResize);
      if (!this.resizingKey) return;
      this.resizingKey = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      colWidthsStore.save(this.columnWidths);
    },
    resetWidth(key) {
      const fallback = COLUMNS.find((c) => c.key === key)?.width;
      if (fallback === undefined) return;
      this.columnWidths = { ...this.columnWidths, [key]: fallback };
      colWidthsStore.save(this.columnWidths);
    },
    tenantStyle(name) {
      return tenantColorFor(name);
    },
    parentName(parentId) {
      if (!parentId) return '';
      return this.parentMap[parentId] || '';
    },
    isAncestorOnly(item) {
      return this.hasActiveFilters && !this.filterMatchIds.has(item.id);
    },
    commentCount(item) {
      return this.commentCounts[item.id] ?? item.comment_count ?? 0;
    },
    statusOptionsFor(item) {
      return selectableStatusesFor(item).map((value) => ({ value, label: STATUS_LABELS[value] || value, chipClass: STATUS_CLASSES[value] }));
    },
    statusClass(status) {
      return STATUS_CLASSES[status] || STATUS_CLASSES.draft;
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    priorityClass(priority) {
      return priorityClassFor(priority);
    },
    priorityLabel(priority) {
      return priorityShortLabel(priority);
    },
    typeClass(type) {
      return TYPE_CLASSES[type] || 'bg-gray-100 text-gray-600';
    },
    dueDateClass(date) {
      if (!date) return 'text-gray-500';
      const diff = daysUntil(date);
      if (diff == null) return 'text-gray-500';
      if (diff < 0) return 'text-red-500 font-medium';
      if (diff <= 3) return 'text-amber-500 font-medium';
      if (isDueThisBusinessWeek(date)) return 'text-yellow-500 font-medium';
      return 'text-gray-500';
    },
    formatDueDate(date) {
      return date ? formatDateValue(date) : '';
    },
  },
};
</script>
